import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ANALYTICS_LOGGING_API_CONFIG, AnalyticsLoggingConfig } from '../config';
import {
  PlatformEventQueryParams,
  SecurityEventQueryParams,
  StrugglingUsersQueryParams,
  PerformanceMetricsQueryParams,
  OrganizationActivityQueryParams,
  PaginatedEventsResponse,
  PlatformOverviewResponse,
  OrganizationsActivityResponse,
  SecurityEventsResponse,
  StrugglingUsersResponse,
  PerformanceMetricsResponse
} from '../models';

/**
 * Service for Platform Admin Analytics
 * 
 * This service provides comprehensive methods for platform administrators to:
 * - Monitor cross-organization activity
 * - View security events and suspicious activity
 * - Identify struggling users for proactive support
 * - Track platform-wide performance metrics
 * - Query all events with advanced filtering
 * 
 * @remarks
 * - Requires Platform Admin authentication (Firebase JWT + email in PLATFORM_ADMIN_EMAILS)
 * - All endpoints are scoped to entire platform (cross-organization)
 * - Rate limited to 100 requests per 15 minutes
 * 
 * @example
 * ```typescript
 * // Inject the service
 * private platformAnalytics = inject(PlatformAdminAnalyticsService);
 * 
 * // Get platform overview
 * this.platformAnalytics.getOverview({ hours: 24 }).subscribe(response => {
 *   console.log('Total events:', response.data.statistics.totalEvents);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class PlatformAdminAnalyticsService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ANALYTICS_LOGGING_API_CONFIG);
  private readonly apiUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/admin/analytics`;
    this.logDebug('PlatformAdminAnalyticsService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // PLATFORM OVERVIEW
  // ============================================================================

  /**
   * Get platform-wide analytics overview
   * 
   * @param params - Query parameters (optional)
   * @returns Observable of platform overview statistics
   * 
   * @example
   * ```typescript
   * this.platformAnalytics.getOverview({ hours: 48 }).subscribe(response => {
   *   const stats = response.data.statistics;
   *   console.log('Total events:', stats.totalEvents);
   *   console.log('Active users:', stats.activeUsers);
   *   console.log('Active orgs:', stats.activeOrganizations);
   * });
   * ```
   */
  getOverview(params?: { hours?: number }): Observable<PlatformOverviewResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/overview`;
    
    this.logDebug('GET platform overview', { url, params });
    
    return this.http.get<PlatformOverviewResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getOverview', error))
    );
  }

  // ============================================================================
  // PLATFORM EVENTS QUERY
  // ============================================================================

  /**
   * Query all events across the platform with advanced filtering
   * 
   * Supports:
   * - Wildcard event types (e.g., 'auth.*')
   * - Comma-separated values (e.g., 'user.login,user.logout')
   * - Date ranges or hour-based filtering
   * - Pagination
   * 
   * @param params - Query parameters for filtering and pagination
   * @returns Observable of paginated events
   * 
   * @example
   * ```typescript
   * // Get all authentication events in last 7 days
   * this.platformAnalytics.queryEvents({
   *   eventType: 'auth.*',
   *   hours: 168,
   *   page: 1,
   *   limit: 100
   * }).subscribe(response => {
   *   console.log('Events:', response.data.events);
   *   console.log('Total:', response.data.pagination.totalEvents);
   * });
   * 
   * // Get critical errors for specific organization
   * this.platformAnalytics.queryEvents({
   *   severity: 'error,critical',
   *   orgId: 'org_123',
   *   hours: 24
   * }).subscribe(response => {
   *   console.log('Critical issues:', response.data.events);
   * });
   * ```
   */
  queryEvents(params?: PlatformEventQueryParams): Observable<PaginatedEventsResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/events`;
    
    this.logDebug('GET platform events', { url, params });
    
    return this.http.get<PaginatedEventsResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('queryEvents', error))
    );
  }

  // ============================================================================
  // ORGANIZATIONS ACTIVITY
  // ============================================================================

  /**
   * Get activity metrics for all organizations
   * 
   * @param params - Query parameters (optional)
   * @returns Observable of organizations activity
   * 
   * @example
   * ```typescript
   * this.platformAnalytics.getOrganizationsActivity({ hours: 24 }).subscribe(response => {
   *   const orgs = response.data.organizations;
   *   orgs.forEach(org => {
   *     console.log(`${org.orgName}: ${org.totalEvents} events, ${org.errorCount} errors`);
   *   });
   * });
   * ```
   */
  getOrganizationsActivity(params?: OrganizationActivityQueryParams): Observable<OrganizationsActivityResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/organizations`;
    
    this.logDebug('GET organizations activity', { url, params });
    
    return this.http.get<OrganizationsActivityResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getOrganizationsActivity', error))
    );
  }

  // ============================================================================
  // SECURITY EVENTS
  // ============================================================================

  /**
   * Get security events and suspicious activity
   * 
   * @param params - Query parameters (optional)
   * @returns Observable of security events
   * 
   * @example
   * ```typescript
   * this.platformAnalytics.getSecurityEvents({
   *   severity: 'critical',
   *   hours: 24
   * }).subscribe(response => {
   *   console.log('Critical security events:', response.data.events);
   *   console.log('Suspicious IPs:', response.data.summary.suspiciousIPs);
   * });
   * ```
   */
  getSecurityEvents(params?: SecurityEventQueryParams): Observable<SecurityEventsResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/security-events`;
    
    this.logDebug('GET security events', { url, params });
    
    return this.http.get<SecurityEventsResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getSecurityEvents', error))
    );
  }

  // ============================================================================
  // STRUGGLING USERS
  // ============================================================================

  /**
   * Identify users experiencing high error rates
   * 
   * @param params - Query parameters (optional)
   * @returns Observable of struggling users
   * 
   * @example
   * ```typescript
   * this.platformAnalytics.getStrugglingUsers({
   *   hours: 24,
   *   minErrors: 10
   * }).subscribe(response => {
   *   console.log('Users needing support:', response.data.users);
   *   response.data.users.forEach(user => {
   *     console.log(`${user.userEmail}: ${user.errorCount} errors`);
   *   });
   * });
   * ```
   */
  getStrugglingUsers(params?: StrugglingUsersQueryParams): Observable<StrugglingUsersResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/struggling-users`;
    
    this.logDebug('GET struggling users', { url, params });
    
    return this.http.get<StrugglingUsersResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getStrugglingUsers', error))
    );
  }

  // ============================================================================
  // PERFORMANCE METRICS
  // ============================================================================

  /**
   * Get platform-wide performance metrics
   * 
   * @param params - Query parameters (optional)
   * @returns Observable of performance metrics
   * 
   * @example
   * ```typescript
   * this.platformAnalytics.getPerformanceMetrics({ hours: 24 }).subscribe(response => {
   *   const metrics = response.data.metrics;
   *   console.log('Avg response time:', metrics.averageResponseTime);
   *   console.log('Error rate:', metrics.errorRate);
   *   console.log('Slowest endpoints:', metrics.slowestEndpoints);
   * });
   * ```
   */
  getPerformanceMetrics(params?: PerformanceMetricsQueryParams): Observable<PerformanceMetricsResponse> {
    const httpParams = this.buildHttpParams(params);
    const url = `${this.apiUrl}/performance`;
    
    this.logDebug('GET performance metrics', { url, params });
    
    return this.http.get<PerformanceMetricsResponse>(url, { params: httpParams }).pipe(
      catchError(error => this.handleError('getPerformanceMetrics', error))
    );
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Build HTTP params from query parameters object
   */
  private buildHttpParams(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();
    
    if (params) {
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== undefined && value !== null && value !== '') {
          httpParams = httpParams.set(key, String(value));
        }
      });
    }
    
    return httpParams;
  }

  /**
   * Handle HTTP errors
   */
  private handleError(method: string, error: any): Observable<never> {
    console.error(`PlatformAdminAnalyticsService.${method} error:`, error);
    
    let errorMessage = 'An error occurred while fetching analytics data';
    
    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    if (error.status === 401) {
      errorMessage = 'Unauthorized. Platform administrator access required.';
    } else if (error.status === 403) {
      errorMessage = 'Forbidden. You do not have platform admin privileges.';
    } else if (error.status === 429) {
      errorMessage = 'Rate limit exceeded. Please try again later.';
    }
    
    return throwError(() => new Error(errorMessage));
  }

  /**
   * Debug logging
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[PlatformAdminAnalytics] ${message}`, data || '');
    }
  }
}
